import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDPagination from 'components/MDPagination';
import Icon from '@mui/material/Icon';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import { apiGetSPointList, apiDoShoppingStatus } from '../../api/point.api'; // API 호출 함수 임포트
import MDBadge from 'components/MDBadge';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';
import { handleDownloadExcel } from '../../components/excel/excelUtils';
import MDButton from '../../components/MDButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const getTodayYmd = () => new Date().toISOString().split('T')[0];

const formatDateYmd = (dateString) => {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return new Date(dateString)
    .toLocaleString('ko-KR', options)
    .replace(/. /g, '-')
    .replace('.', '')
    .replace(' ', ' ')
    .slice(0, 10);
};

const ShoppingPointList = () => {
  const [shoppingPoints, setShoppingPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [userid, setUserId] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const { user, appIsReady } = useData();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const today = getTodayYmd(); // YYYY-MM-DD
    setCreatedFrom(today);
    setCreatedTo(today);
  }, []);

  useEffect(() => {
    if (createdFrom && createdTo && appIsReady && user) {
      fetchShoppingPoints(page);
    }
  }, [page, user, appIsReady, createdFrom, createdTo]);

  const fetchShoppingPoints = async (pageNum) => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        limit: 10,
        userid,
        createdFrom,
        createdTo,
      };
      const response = await apiGetSPointList(params, user.accessToken);
      console.log("response=" + JSON.stringify(response));
      if (response && response.data) {
        setShoppingPoints(response.data);
        setTotal(response.total); // 전체 데이터 수 설정
      } else {
        console.log('No data received');
      }
    } catch (error) {
      console.error('Failed to load shopping points:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNum) => {
    setPage(pageNum);
  };

  const handleSearch = () => {
    fetchShoppingPoints(1); // 새 검색을 시작할 때 첫 페이지로 설정
  };

  const handleConvertClick = async (pstatus, point) => {
    const params = {
      userid: point.userid,
      point: point.point,
      status: pstatus
    };
    try {
      const response = await apiDoShoppingStatus(params, point.id, user.accessToken);
      if (response === 'success') {
        setSnackbarMessage('전환 처리되었습니다.');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        fetchShoppingPoints(page); // Refresh the list after successful transfer
      } else {
        setSnackbarMessage('전환 처리에 실패했습니다.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage('전환 처리 중 오류가 발생했습니다.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const rows = shoppingPoints.map(point => ({
    id: point.id,
    shopid: point.shopid,
    userid: point.userid,
    useremail: point.userEmail,
    point: point.point,
    status: (
      <MDBadge
        badgeContent={point.status}
        color={point.status === 'approved' ? 'success' : point.status === 'canceled' ? 'error' : 'secondary'}
        variant="gradient"
        size="sm"
      />
    ),
    createdAt: formatDateYmd(point.createdAt),
    convert: point.status !== 'approved' ? (
      <Button 
        variant="contained" 
        color="success" 
        size="small" 
        onClick={() => handleConvertClick('approved', point)}
      >
        전환처리
      </Button>
    ) : (
      <Button 
        variant="outline" 
        color="primary" 
        size="small" 
        onClick={() => handleConvertClick('canceled', point)}
      >
        취소처리
      </Button>
    )

  }));

  const columns = [
    { Header: "ID", accessor: "id", align: "left" },
    { Header: "Shop ID", accessor: "shopid", align: "left" },
    { Header: "User ID", accessor: "userid", align: "left" },
    { Header: "email", accessor: "useremail", align: "left" },
    { Header: "Points", accessor: "point", align: "left" },
    { Header: "Status", accessor: "status", align: "left" },
    { Header: "Created At", accessor: "createdAt", align: "left" },
    { Header: "전환처리", accessor: "convert", align: "center" },
  ];

  const totalPages = Math.ceil(total / 10);
  const maxPageNumbers = 10;
  const currentPageGroup = Math.floor((page - 1) / maxPageNumbers);
  const startPage = currentPageGroup * maxPageNumbers + 1;
  const endPage = Math.min(startPage + maxPageNumbers - 1, totalPages);
  const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);

  return (
    <DashboardLayout>
      <TopNavbar />
      <MDBox display="flex" mr={2} justifyContent="right">
        <MDButton variant="gradient" color="info" onClick={() => handleDownloadExcel('쇼핑 포인트 내역', columns, rows)} sx={{ ml: 2 }}>엑셀 다운로드</MDButton>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  쇼핑포인트 전환내역
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label="User ID"
                          fullWidth
                          value={userid}
                          onChange={(e) => setUserId(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label="Created From"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={createdFrom}
                          onChange={(e) => setCreatedFrom(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField
                          label="Created To"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={createdTo}
                          onChange={(e) => setCreatedTo(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          onClick={handleSearch}
                        >
                          검색
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
              {/**
              <MDBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="center"
                alignItems={{ xs: "flex-start", sm: "center" }}
                p={3}
              >
                <MDPagination
                  variant="gradient"
                  color="info"
                >
                  {page > 1 && (
                    <MDPagination item onClick={() => handlePageChange(page - 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                    </MDPagination>
                  )}
                  {pageNumbers.map(pageNum => (
                    <MDPagination
                      item
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      active={page === pageNum}
                    >
                      {pageNum}
                    </MDPagination>
                  ))}
                  {page < totalPages && (
                    <MDPagination item onClick={() => handlePageChange(page + 1)}>
                      <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                    </MDPagination>
                  )}
                </MDPagination>
              </MDBox>
               */}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default ShoppingPointList;
